import React, { useEffect, lazy, useState, useRef } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Footer from "../../Footer/Footer";


const MainTemplate = () => {
  const { slug, language = "en" } = useParams();

  const homePage = useSelector((state) => state.homePage);
  const meta = homePage?.post_meta;

  // State for popup visibility
  const [showEventPopup, setShowEventPopup] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  // Ref for modal content
  const modalContentRef = useRef(null);

  // Check if we should show the popups
  useEffect(() => {
    // Show event popup if enabled
    if (meta?.show_popup_for_event && meta.show_popup_for_event !== "0") {
      setShowEventPopup(true);
    }
  }, [meta]);

  const updateMetaTags = (title, description, keywords) => {
    document.title = title || "Default Title";

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = description || "Default description";

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.name = "keywords";
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = keywords || "default,keywords";

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = title;

    const ogDescription = document.querySelector(
      'meta[property="og:description"]'
    );
    if (ogDescription) ogDescription.content = description;
  };

  useEffect(() => {
    const post_meta = homePage?.post_meta;
    if (post_meta) {
      updateMetaTags(
        post_meta.nfdc_meta_title,
        post_meta.nfdc_meta_description,
        post_meta.nfdc_meta_keywords
      );
    }
  }, [homePage]);

  // Handle start quiz button click
  const handleStartQuiz = () => {
    console.log("Starting vigilance quiz");
    // Navigate to quiz page
    window.location.href = `/quiz/${language}`;
  };

  // Handle event popup close
  const handleCloseEventPopup = () => {
    setShowEventPopup(false);
    setActiveSlide(0);
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (modalContentRef.current && !modalContentRef.current.contains(e.target)) {
      handleCloseEventPopup();
    }
  };

  // Handle slide change
  const goToSlide = (index) => {
    setActiveSlide(index);
  };

  const totalSlides = 3; // Now 3 slides: Quiz, Event Image 1, Event Image 2

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  // Inline CSS styles
  const styles = `
    /* Modal Animation */
    .modal.show {
      animation: slideInUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }

    @keyframes slideInUp {
      from {
        opacity: 0;
        transform: translateY(30px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    /* Modal Dialog */
    .custom-modal-dialog {
      max-width: 800px;
    }

    @media (min-width: 768px) {
      .custom-modal-dialog {
        max-width: 800px;
      }
    }

    @media (min-width: 992px) {
      .custom-modal-dialog {
        max-width: 800px;
      }
    }

    /* Modal Content */
    .modal-content {
      border: none;
      border-radius: 20px;
      overflow: hidden;
      background: transparent;
      position: relative;
    }

    .modal-body {
      padding: 0;
      position: relative;
    }

    /* Close Button */
    .modal-close-btn {
      position: absolute;
      top: 15px;
      right: 15px;
      background: rgba(255, 255, 255, 0.9);
      color: #333;
      border: none;
      width: 35px;
      height: 35px;
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      font-size: 20px;
      transition: all 0.3s ease;
      opacity: 1;
    }

    .modal-close-btn:hover {
      background: #fff;
      transform: rotate(90deg);
    }

    /* Carousel Styles */
    .carousel-container {
      position: relative;
      background: #1f2937;
      border-radius: 20px;
      overflow: hidden;
    }

    .carousel-slide {
      display: none;
      width: 100%;
    }

    .carousel-slide.active {
      display: block;
    }

    /* Quiz Slide Styles - Original Dialog Style */
    .quiz-slide-content {
      background: white;
      padding: 65px;
      border-radius: 20px;
      text-align: left;
      min-height: 300px;
      text-align: center;
    }

    .quiz-slide-content h2 {
      color: #1f2937;
      margin-bottom: 10px;
      font-size: 24px;
      font-weight: bold;
    }

    .quiz-slide-content p {
      color: #6b7280;
      margin-bottom: 20px;
      font-size: 16px;
      line-height: 1.6;
    }

    /* Original Button Style - Same as before */
    .quiz-slide-content .btn-original {
      display: inline-block;
      padding: 12px 30px;
      background: #1976d2;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      text-decoration: none;
      transition: all 0.3s ease;
      text-align: center;
    }

    .quiz-slide-content .btn-original:hover {
      background: #155fa0;
      box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
    }

    /* Carousel Controls */
    .carousel-control {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 100;
      background: rgba(0, 0, 0, 0.5);
      border: none;
      border-radius: 50%;
      width: 45px;
      height: 45px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      color: white;
      font-size: 24px;
    }

    .carousel-control:hover {
      background: rgba(0, 0, 0, 0.8);
    }

    .carousel-control-prev {
      left: 15px;
    }

    .carousel-control-next {
      right: 15px;
    }

    /* Carousel Indicators */
    .carousel-indicators {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 10px;
      z-index: 100;
    }

    .carousel-indicator {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: none;
      background: rgba(255, 255, 255, 0.5);
      cursor: pointer;
      transition: all 0.3s ease;
      padding: 0;
    }

    .carousel-indicator.active {
      background: #fff;
      transform: scale(1.2);
    }

    /* Image styles */
    .carousel-slide img {
      width: 100%;
      display: block;
    }

    /* Responsive */
    @media screen and (min-width: 320px) and (max-width: 767px) {
      .carousel-control {
        width: 35px;
        height: 35px;
        font-size: 18px;
      }
      
      .quiz-slide-content {
        padding: 20px;
        min-height: 200px;
      }
      
      .quiz-slide-content h2 {
        font-size: 20px;
      }
      
      .quiz-slide-content p {
        font-size: 14px;
      }
      
      .quiz-slide-content .btn-original {
        padding: 10px 20px;
        font-size: 14px;
        width: 100%;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <div className="text-container">
        <Outlet />
      </div>

      {/* Event Popup with Quiz as Slide 1 */}
      {showEventPopup && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          role="dialog"
          onClick={handleBackdropClick}
          style={{
            backgroundColor: 'rgba(0,0,0,0.85)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div className="modal-dialog modal-dialog-centered custom-modal-dialog" role="document">
            <div className="modal-content" ref={modalContentRef}>
              {/* Close Button */}
              <button
                className="modal-close-btn"
                onClick={handleCloseEventPopup}
                aria-label="Close"
                data-bs-dismiss="modal"
              >
                ✕
              </button>

              <div className="">
                <div className="carousel-container">
                  {/* Slide 1 - Quiz Popup */}
                  <div className={`carousel-slide ${activeSlide === 0 ? 'active' : ''}`}>
                    <div className="quiz-slide-content">
                      <h2>{meta?.vigilance_week_heading ?? "Vigilance Awareness"}</h2>
                      <p>{meta?.vigilance_description || "Stay vigilant and aware of your surroundings."}</p>
                      <Link
                        to={`/quiz/${language}`}
                        className="btn-original"
                        onClick={handleStartQuiz}
                        role="link"
                        aria-label={meta?.vigilance_start_quiz_button}
                        aria-labelledby={meta?.vigilance_start_quiz_button}
                      >
                        {meta?.vigilance_start_quiz_button || "Start Quiz"}
                      </Link>
                    </div>
                  </div>

                  {/* Slide 2 - Event Image 1 */}
                  <div className={`carousel-slide ${activeSlide === 1 ? 'active' : ''}`}>
                    <a href={meta?.show_popup_event_link_1 || '#'} target="_blank" rel="noopener noreferrer">
                      <img
                        src={meta?.show_popup_event_image_1 || 'https://nfdcindia.com/nfdcapi/api/gallery/1783944216443_7121841.jpeg'}
                        alt="Banner 1"
                        className="img-fluid"
                        style={{ width: '100%', display: 'block' }}
                      />
                    </a>
                  </div>

                  {/* Slide 3 - Event Image 2 */}
                  <div className={`carousel-slide ${activeSlide === 2 ? 'active' : ''}`}>
                    <a href={meta?.show_popup_event_link || '#'} target="_blank" rel="noopener noreferrer">
                      <img
                        src={meta?.show_popup_event_image_1 || 'https://nfdcindia.com/nfdcapi/api/gallery/1783944216443_7121841.jpeg'}
                        alt="Banner 2"
                        className="img-fluid"
                        style={{ width: '100%', display: 'block' }}
                      />
                    </a>
                  </div>

                  {/* Carousel Controls */}
                  <button
                    className="carousel-control carousel-control-prev"
                    onClick={prevSlide}
                    aria-label="Previous"
                  >
                    ‹
                  </button>
                  <button
                    className="carousel-control carousel-control-next"
                    onClick={nextSlide}
                    aria-label="Next"
                  >
                    ›
                  </button>

                  {/* Carousel Indicators */}
                  <div className="carousel-indicators">
                    <button
                      className={`carousel-indicator ${activeSlide === 0 ? 'active' : ''}`}
                      onClick={() => goToSlide(0)}
                      aria-label="Slide 1 - Quiz"
                    />
                    <button
                      className={`carousel-indicator ${activeSlide === 1 ? 'active' : ''}`}
                      onClick={() => goToSlide(1)}
                      aria-label="Slide 2"
                    />
                    <button
                      className={`carousel-indicator ${activeSlide === 2 ? 'active' : ''}`}
                      onClick={() => goToSlide(2)}
                      aria-label="Slide 3"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default MainTemplate;